import { Alert, Button, DataTable, Text } from '@ui/themed';
import { t } from 'i18next';
import { useEffect, useMemo, useState } from 'react';
import type { Platform } from '../../../context/env-context';
import { I18N_COMPONENTS, I18N_PAGE } from '../constants';
import type { ProductSearchRow } from '../product-search-rows';
import { toProductSearchRows } from '../product-search-rows';
import type { Product } from '../types/product';
import { productSearchColumns } from './product-search-columns';

type ProductSearchProps = {
  searchTerm: string;
  debouncedTerm: string;
  results: Product[] | undefined;
  isFetching: boolean;
  isError: boolean;
  onRetry: () => void;
  platform?: Platform;
  onSearchTermChange: (term: string) => void;
  onSelectVariant: (variantSku: string) => void;
};

export function ProductSearch({
  searchTerm,
  debouncedTerm,
  results,
  isFetching,
  isError,
  onRetry,
  platform,
  onSearchTermChange,
  onSelectVariant,
}: ProductSearchProps) {
  const rows = useMemo(() => toProductSearchRows(results), [results]);
  const columns = useMemo(() => productSearchColumns({ platform }), [platform]);

  const [expanded, setExpanded] = useState<string[]>([]);
  // Search results arrive as a whole new set, so open every parent that has variants, matching the
  // old panel where nested variants were always visible.
  useEffect(() => {
    setExpanded(rows.filter((row) => row.children.length > 0).map((row) => row.id));
  }, [rows]);

  const hasTerm = searchTerm.trim().length > 0;
  const isDebouncing = searchTerm.trim() !== debouncedTerm.trim();

  return (
    <div className="flex flex-col gap-4">
      {isError && (
        <Alert variant="error" headingLevel="h3">
          <div className="flex w-full flex-col items-start gap-2">
            <Text>{t(`${I18N_COMPONENTS}.product_search.search_error`)}</Text>
            <Button variant="secondary" onClick={onRetry}>
              {t('buttons:reload')}
            </Button>
          </div>
        </Alert>
      )}
      <DataTable<ProductSearchRow>
        caption={t(`${I18N_COMPONENTS}.product_search.caption`)}
        columns={columns}
        data={rows}
        getRowId={(row) => row.id}
        getSubRows={(row) =>
          row.kind === 'parent' && row.children.length > 0 ? row.children : undefined
        }
        expansion={{ value: expanded, onChange: setExpanded }}
        search={{
          value: searchTerm,
          placeholder: t(`${I18N_PAGE}.search_placeholder`),
          onChange: onSearchTermChange,
        }}
        loading={hasTerm && (isDebouncing || isFetching)}
        skeleton={{ rows: 3 }}
        onRowClick={(row) => {
          // A parent row only gets a click when it has no children, so a single variant, and a
          // product with no variants has nothing to open.
          const sku =
            row.kind === 'variant'
              ? row.variant.identifiers.sku
              : row.product.variants[0]?.identifiers.sku;
          if (sku) {
            onSelectVariant(sku);
          }
        }}
      />
    </div>
  );
}
